import React, { useRef, useCallback, useMemo, useState } from "react";
import { Group, Rect, Text } from "react-konva";
import Konva from "konva";
import { IMondayTaskController, MondayItemColumn } from "../../controller";
import { FONT_FAMILY, FONT_SIZE, COLUMN_HEIGHT, COLUMN_PADDING, COLUMN_WIDTH } from "../../consts";
import { StatusPicker, StatusOption } from "./pickers/status-picker";
import { useMondayTaskInteraction } from "../../hooks/use-monday-task-interaction";
import { useAtomValue } from "jotai";
import { stageRefAtom } from "state-atoms/general-atoms";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import tracking from "frontend/tracking";

interface StatusColumnProps {
  column: MondayItemColumn;
  x: number;
  controller: IMondayTaskController;
}

export const StatusColumn = React.memo(({ column, x, controller }: StatusColumnProps) => {
  const stageRef = useAtomValue(stageRefAtom);
  const stage = stageRef?.current || null;
  const groupRef = useRef<Konva.Group>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const statusValue = column.value as { color?: string } | undefined;
  const color = statusValue?.color ?? "#C4C4C4";
  const text = getStatusDisplayValue(column);
  const textWidth = COLUMN_WIDTH - 2 * COLUMN_PADDING;

  const boardColumns = controller.getBoardColumns();
  const memoizedBoardColumns = useDeepEqualMemo(boardColumns);

  // Get status options from board columns
  const statusOptions = useMemo(() => {
    if (!memoizedBoardColumns) return [];

    const columnDefinition = memoizedBoardColumns.find((col) => col.id === column.id);
    if (!columnDefinition || columnDefinition.type !== "status" || !columnDefinition.settings) return [];

    const { labels, labels_colors, labels_positions_v2 } = columnDefinition.settings;
    if (!labels || !labels_colors) return [];

    const statuses = Object.keys(labels).map((index) => ({
      index: Number(index),
      label: labels[index],
      color: labels_colors[index]?.color || "#C4C4C4",
    }));

    // Default status
    if (!statuses.some((status) => status.index === 5)) {
      statuses.push({
        index: 5, // This is ALWAYS the ID of the default status.
        label: "",
        color: "#C4C4C4",
      });
    }

    if (labels_positions_v2) {
      statuses.sort((a, b) => labels_positions_v2[a.index] - labels_positions_v2[b.index]);
    }

    return statuses;
  }, [column.id, memoizedBoardColumns]);

  const handleClick = useCallback(() => {
    setPickerOpen((previous) => !previous);
  }, []);

  const handleStatusSelect = useCallback(
    async (option: StatusOption) => {
      tracking.trackAnalyticsEvent("monday_task_column_updated", {
        columnType: "status",
      });
      await controller.updateColumnValue(column.id, option, { id: column.id, type: "status" });
      setPickerOpen(false);
    },
    [column.id, controller]
  );

  const { onMouseEnter, onMouseLeave, onMouseDown, onMouseUp } = useMondayTaskInteraction({
    elementRef: groupRef,
    onClick: handleClick,
  });

  return (
    <>
      <Group
        x={x}
        ref={groupRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
      >
        <Rect width={COLUMN_WIDTH} height={COLUMN_HEIGHT} fill={color} cornerRadius={6} />
        <Text
          x={COLUMN_PADDING}
          y={1}
          text={text}
          fontSize={FONT_SIZE}
          fill="#FFF"
          fontFamily={FONT_FAMILY}
          letterSpacing={0.124}
          width={textWidth}
          height={COLUMN_HEIGHT}
          align="center"
          verticalAlign="middle"
          ellipsis={true}
          wrap="none"
        />

        <StatusPicker
          konvaNode={groupRef.current}
          stage={stage}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          options={statusOptions}
          onSelect={handleStatusSelect}
          buttonRef={groupRef}
        />
      </Group>
    </>
  );
});

StatusColumn.displayName = "StatusColumn";

function getStatusDisplayValue(column: MondayItemColumn): string {
  const statusValue = column.value as { text?: string; label?: string } | undefined;
  return statusValue?.text || statusValue?.label || "Unset";
}
